import React from 'react';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { Badge } from '../userProject/UpUi';
import { DETAILS, selectedField, setMethods, setValue } from './espremium';

/** Interrupteur (rond qui glisse). */
export const Switch = ({ on, onChange, disabled = false, label }) => (
    <button type='button' role='switch' aria-checked={on} aria-label={label} title={label} className={`pset-switch ${on ? 'on' : ''}`} disabled={disabled} onClick={() => onChange(!on)}>
        <span />
    </button>
)

/**
 * Un projet : paiement ouvert ou non, ses moyens proposés (dans cet ordre, avec le compte utilisé pour un moyen
 * à plusieurs comptes comme stripe_link), puis les autres moyens à ajouter. save(clé, action(socket), message) : enregistre puis relit.
 */
const ProjectMethods = ({ project: p, config, busy, save }) => {
    const all = config.METHODPAY || {}
    // liste absente : ESPREMIUM lancé avant la mise à jour (ne connaît pas ce projet) → rien de modifiable, sinon on écraserait sa liste
    const supported = Array.isArray(config[p.list])
    const active = supported ? config[p.list] : []
    const inactive = Object.keys(all).filter((k) => !active.includes(k))
    const open = Boolean(config[p.open])
    const saving = !supported || Boolean(busy?.startsWith(`${p.id}:`))

    // conf affichée : pour un moyen à plusieurs comptes, celle du compte choisi pour ce projet
    const confOf = (k) => {
        const raw = all[k]
        if (!Array.isArray(raw)) return raw || {}
        return raw[Number(config[selectedField(k, p.suffix)]) || 0] || raw[0] || {}
    }

    const saveList = (names, done) => save(`${p.id}:methods`, (socket) => setMethods(socket, p.config, names), done)
    const toggle = (k, on) => saveList(on ? [...active, k] : active.filter((m) => m !== k), `${p.name} : ${k} ${on ? 'ajouté' : 'retiré'}`)
    const move = (i, dir) => {
        const next = [...active]
        ;[next[i], next[i + dir]] = [next[i + dir], next[i]]
        saveList(next, `${p.name} : ordre enregistré`)
    }
    const setOpen = (on) => save(`${p.id}:open`, (socket) => setValue(socket, p.open, on), `${p.name} : paiement ${on ? 'ouvert' : 'fermé'}`)
    const setAccount = (k, idx) => save(`${p.id}:account`, (socket) => setValue(socket, selectedField(k, p.suffix), idx), `${p.name} : compte ${all[k][idx]?.name || idx + 1} choisi`)

    const row = (k, i) => {
        const c = confOf(k)
        const accounts = Array.isArray(all[k]) ? all[k] : null
        return (
            <li key={k} className='pset-method'>
                <span className='pset-pos'>{i + 1}</span>
                <div className='pset-method-text'>
                    <b>
                        {c.label || k} {!all[k] && <Badge tone='ko'>absent de METHODPAY</Badge>}
                    </b>
                    <small>
                        <code>{k}</code>
                        {c.type_details?.length > 0 && ` · ${c.type_details.map((d) => DETAILS[d] || d).join(', ')}`}
                    </small>
                    <span className='up-badges'>
                        <Badge tone={c.is_auto ? 'ok' : 'warn'}>{c.is_auto ? 'Automatique' : 'Validé à la main'}</Badge>
                        {c.tax > 0 && <Badge tone='gold'>+ {c.tax} € de frais</Badge>}
                    </span>
                    {accounts && (
                        <label className='pset-account'>
                            Compte
                            <select value={Number(config[selectedField(k, p.suffix)]) || 0} disabled={saving} onChange={(e) => setAccount(k, Number(e.target.value))}>
                                {accounts.map((it, idx) => (
                                    <option key={idx} value={idx}>
                                        {it.name || `Compte ${idx + 1}`}
                                    </option>
                                ))}
                            </select>
                            {p.shared && <small>commun avec {p.shared}</small>}
                        </label>
                    )}
                </div>
                <div className='pset-move'>
                    <button type='button' disabled={saving || i === 0} onClick={() => move(i, -1)} title='Monter'>
                        <ArrowUp size={14} />
                    </button>
                    <button type='button' disabled={saving || i === active.length - 1} onClick={() => move(i, 1)} title='Descendre'>
                        <ArrowDown size={14} />
                    </button>
                </div>
                <button type='button' className='pset-remove' disabled={saving} onClick={() => toggle(k, false)} title={`Retirer ${k}`}>
                    <X size={16} />
                </button>
            </li>
        )
    }

    return (
        <article className={`pset-project ${open ? 'open' : ''}`}>
            <header>
                <div className='pset-title'>
                    <h3>{p.name}</h3>
                    <Badge tone={open ? 'ok' : 'ko'}>{open ? 'Paiement ouvert' : 'Paiement fermé'}</Badge>
                    <Switch on={open} disabled={saving} onChange={setOpen} label={open ? 'Fermer le paiement' : 'Ouvrir le paiement'} />
                </div>
                <small>
                    {p.hint} · <code>{p.config}</code>
                </small>
            </header>
            {!supported ? (
                <p className='pset-warn'>ESPREMIUM ne connaît pas encore ce projet : relance-le avec la dernière version de server.js.</p>
            ) : active.length ? (
                <ul className='pset-methods'>{active.map((k, i) => row(k, i))}</ul>
            ) : (
                <p className='pset-warn'>Aucun moyen de paiement proposé : les users ne peuvent pas payer.</p>
            )}
            {supported && inactive.length > 0 && (
                <div className='pset-add'>
                    <span>Ajouter</span>
                    {inactive.map((k) => {
                        const c = confOf(k)
                        return (
                            <button key={k} type='button' disabled={saving} onClick={() => toggle(k, true)} title={[c.label, c.type_details?.map((d) => DETAILS[d] || d).join(', ')].filter(Boolean).join(' · ')}>
                                <Plus size={13} /> {k}
                                {!c.is_auto && <small>manuel</small>}
                            </button>
                        )
                    })}
                </div>
            )}
        </article>
    )
}

export default ProjectMethods
